/**
 * One parser per broker dispatch form. Each reads the PDF layout and returns
 * the dispatch exactly as written; mapping onto load board names happens in
 * normalize.ts.
 */
import { Layout, LINE_TOLERANCE, findItem, joinItems } from './layout';

export type BrokerKey = 'titan' | 'avfuel';

export interface ParsedDispatch {
  brokerKey: BrokerKey;
  loadNumber: string;
  orderNumber: string;
  poNumber: string;
  /** As printed, MM/DD/YYYY */
  deliveryDate: string;
  deliveryWindow: string;
  supplier: string;
  terminalName: string;
  terminalCity: string;
  products: Array<{ product: string; gallons: string }>;
  /** null when the form doesn't say */
  pumpRequired: boolean | null;
  destinationName: string;
  destinationAddress: string;
  destinationCityStateZip: string;
  destinationPhone: string;
  instructions: string;
  comments: string;
}

/* --------------------------------- helpers -------------------------------- */

/**
 * Lines of text to the right of a label, from the label's line down until
 * something is printed left of the value column (the next label, a table).
 */
const blockRightOf = (layout: Layout, label: RegExp): string[] => {
  const anchor = findItem(layout.items, label);
  if (!anchor) return [];
  const valueX = anchor.x + 10;
  const valueStart = Math.min(
    anchor.x + 100,
    ...layout.items
      .filter(item => Math.abs(item.y - anchor.y) <= LINE_TOLERANCE && item.x > valueX)
      .map(item => item.x)
  );
  const stopY = Math.max(
    -Infinity,
    ...layout.items
      .filter(item => item.x < valueStart - 10 && item.y < anchor.y - LINE_TOLERANCE)
      .map(item => item.y)
  );
  return layout.lines
    .filter(line => line.y <= anchor.y + LINE_TOLERANCE && line.y > stopY + LINE_TOLERANCE)
    .map(line => joinItems(line.items.filter(item => item.x > valueX)))
    .filter(Boolean);
};

/**
 * Lines printed under a column header, read until the column has a gap taller
 * than a couple of lines (the next header row).
 */
const cellBelow = (layout: Layout, header: RegExp): string[] => {
  const anchor = findItem(layout.items, header);
  if (!anchor) return [];
  const rowHeaders = layout.items
    .filter(item => Math.abs(item.y - anchor.y) <= LINE_TOLERANCE)
    .sort((a, b) => a.x - b.x);
  const next = rowHeaders.find(item => item.x > anchor.x + 5);
  const left = anchor.x - 5;
  const right = next ? next.x - 5 : Infinity;

  const out: string[] = [];
  let lastY = anchor.y;
  for (const line of layout.lines) {
    if (line.y >= anchor.y - LINE_TOLERANCE) continue;
    const cells = line.items.filter(item => item.x >= left && item.x < right);
    if (!cells.length) continue;
    if (lastY - line.y > 16) break;
    out.push(joinItems(cells));
    lastY = line.y;
  }
  return out.filter(Boolean);
};

/**
 * Reads a bordered table: each row item goes to the header whose centre is
 * nearest (cells aren't aligned to their header's left edge).
 */
const readTable = (layout: Layout, firstHeader: RegExp, endLabel: RegExp): Array<Record<string, string>> => {
  const anchor = findItem(layout.items, firstHeader);
  if (!anchor) return [];
  const headers = layout.items
    .filter(item => Math.abs(item.y - anchor.y) <= LINE_TOLERANCE)
    .map(item => ({ ...item, center: item.x + item.width / 2 }));
  const end = findItem(layout.items, endLabel);
  const endY = end ? end.y : -Infinity;

  return layout.lines
    .filter(line => line.y < anchor.y - LINE_TOLERANCE && line.y > endY)
    .map(line => {
      const row: Record<string, string> = {};
      for (const item of line.items) {
        const center = item.x + item.width / 2;
        const header = headers.reduce((best, h) =>
          Math.abs(h.center - center) < Math.abs(best.center - center) ? h : best
        );
        row[header.text] = row[header.text] ? `${row[header.text]} ${item.text}` : item.text;
      }
      return row;
    })
    .filter(row => Object.keys(row).length > 1);
};

/** Splits a ship-to block into name / street / city-state-zip / phone. */
export const splitAddress = (lines: string[]) => {
  const [first = '', ...rest] = lines;
  const cityStateZip = rest.find(l => /,\s*[A-Z]{2}\s+\d{5}/.test(l)) || '';
  const phone = rest.find(l => /\(\d{3}\)\s*\d{3}-\d{4}/.test(l)) || '';
  const address = rest
    .filter(l => l !== cityStateZip && l !== phone)
    .join(', ')
    .replace(/,\s*$/, '');
  return {
    name: first.replace(/,\s*$/, ''),
    address: address.replace(/,\s*,/g, ',').trim(),
    cityStateZip,
    phone: phone.replace(/\s*Ext\.?\s*$/i, '')
  };
};

/**
 * Reads "pump required" from free text when the form has no pump field.
 * "Pump required on Avgas only" only counts when the load carries Avgas.
 */
export const inferPump = (text: string, products: string[]): boolean => {
  const t = text.toLowerCase();
  if (/\bno pump\b/.test(t)) return false;
  const onlyFor = t.match(/pump\s+(?:is\s+)?required\s+(?:on|for)\s+([a-z0-9 ]+?)\s+only/);
  if (onlyFor) {
    const word = onlyFor[1].trim();
    return products.some(product => product.toLowerCase().includes(word));
  }
  return /pump\s+(?:is\s+)?(?:required|needed)|needs?\s+(?:a\s+)?pump/.test(t);
};

/* --------------------------------- parsers -------------------------------- */

/** Titan Aviation Fuels "Carrier Dispatch" form. */
const parseTitan = (layout: Layout): ParsedDispatch => {
  const value = (label: RegExp) => blockRightOf(layout, label).join(' ');
  const shipTo = splitAddress(blockRightOf(layout, /^ShipTo:/i));
  const rows = readTable(layout, /^Supplier Name$/i, /^Comments:/i);
  const products = rows.map(row => ({
    product: row['Dropped Product'] || row['Pulled Product'] || '',
    gallons: row['Gallons Ordered'] || '',
    supplier: row['Supplier Name'] || '',
    terminal: row['Terminal Name'] || ''
  }));

  const commentsAnchor = findItem(layout.items, /^Comments:/i);
  const comments = commentsAnchor
    ? layout.lines
        .filter(line => line.y < commentsAnchor.y - LINE_TOLERANCE)
        .map(line => line.text)
        .filter(text => text && !/^<TOFAXNUM/i.test(text))
        .join(' ')
        .replace(/\s+/g, ' ')
        .replace(/^"|"$/g, '')
        .trim()
    : '';
  const instructions = value(/^Delivery Instructions:/i);

  return {
    brokerKey: 'titan',
    loadNumber: value(/^Load Number:/i),
    orderNumber: value(/^Order Number:/i),
    poNumber: value(/^PO Number:/i),
    deliveryDate: value(/^Delivery Date:/i),
    deliveryWindow: value(/^Delivery Time:/i),
    supplier: products[0]?.supplier || '',
    terminalName: products[0]?.terminal || '',
    terminalCity: products[0]?.terminal || '',
    products: products.map(({ product, gallons }) => ({ product, gallons })),
    pumpRequired: inferPump(`${instructions} ${comments}`, products.map(p => p.product)),
    destinationName: shipTo.name,
    destinationAddress: shipTo.address,
    destinationCityStateZip: shipTo.cityStateZip,
    destinationPhone: shipTo.phone,
    instructions,
    comments
  };
};

/** Avfuel "Dispatch Request" form. */
const parseAvfuel = (layout: Layout): ParsedDispatch => {
  const cell = (header: RegExp) => cellBelow(layout, header).join(' ');
  const location = splitAddress(cellBelow(layout, /^DELIVERY LOCATION$/i));
  const pumpText = (layout.lines.find(l => /PUMP REQUIRED:/i.test(l.text))?.text || '').replace(
    /.*PUMP REQUIRED:\s*/i,
    ''
  );
  const poLine = layout.lines.find(l => /CUSTOMER PO#/i.test(l.text))?.text || '';

  const commentsAnchor = findItem(layout.items, /^SPECIAL COMMENTS:?$/i);
  const comments: string[] = [];
  if (commentsAnchor) {
    for (const line of layout.lines) {
      if (line.y >= commentsAnchor.y - LINE_TOLERANCE) continue;
      if (/^1\.\s/.test(line.text)) break;
      comments.push(line.text);
    }
  }

  return {
    brokerKey: 'avfuel',
    loadNumber: cell(/^RELEASE #$/i),
    orderNumber: '',
    poNumber: poLine
      .replace(/.*CUSTOMER PO#\s*:?\s*/i, '')
      .replace(/PUMP REQUIRED:.*$/i, '')
      .trim(),
    deliveryDate: cell(/^DELIVERY DATE$/i),
    deliveryWindow: cell(/^TIME$/i),
    supplier: cell(/^SUPPLIER$/i),
    terminalName: cell(/^TERMINAL$/i),
    terminalCity: cell(/^CITY$/i),
    products: [{ product: cell(/^PRODUCT$/i), gallons: cell(/^GALLONS$/i) }],
    pumpRequired: /^yes/i.test(pumpText) ? true : /^no/i.test(pumpText) ? false : null,
    destinationName: location.name,
    destinationAddress: location.address,
    destinationCityStateZip: location.cityStateZip,
    destinationPhone: cell(/^DELIVERY CONTACT$/i),
    instructions: '',
    comments: comments.join(' ').trim()
  };
};

/** Picks the broker by what the form itself says. */
export const detectBroker = (text: string): BrokerKey | null => {
  if (/Titan Aviation Fuels/i.test(text) && /Load Number/i.test(text)) return 'titan';
  if (/DISPATCH REQUEST/i.test(text) && /RELEASE #/i.test(text) && /avfuel/i.test(text)) {
    return 'avfuel';
  }
  return null;
};

/** Reads any supported dispatch layout; null when no parser recognises it. */
export const parseDispatchLayout = (layout: Layout): ParsedDispatch | null => {
  const broker = detectBroker(layout.text);
  if (broker === 'titan') return parseTitan(layout);
  if (broker === 'avfuel') return parseAvfuel(layout);
  return null;
};
